import { Theme, ThemeProvider, createTheme, useTheme } from '@mui/material';
import * as coreLocales from '@mui/material/locale';
import * as dataGridLocales from '@mui/x-data-grid/locales';
import {
  LocalizationProvider,
  LocalizationProviderProps,
} from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import * as datePickerLocales from '@mui/x-date-pickers/locales';
import { Moment } from 'moment';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectedLanguageContext } from '~/components/SelectedLanguage/context';
import { useInjectCustomTranslations } from '~/hooks/useInjectCustomTranslations/useInjectCustomTranslations';
import { useSyncedLocalStorage } from '~/hooks/useLocalStorage';
import {
  LANGUAGE_KEY,
  getAdapterLocale,
  getMomentDateLibInstance,
} from '~/localization';

type PropsType = {
  children?: ReactNode;
};

export const LanguageAndLocalizationProvider = (props: PropsType) => {
  const { children } = props;

  const { i18n } = useTranslation();
  const updateTranslationsLanguage = useCallback(
    (language: string | undefined) => {
      if (!!language && i18n.language !== language) {
        i18n.changeLanguage(language);
      }
    },
    [i18n],
  );

  const { value: language, setValue } = useSyncedLocalStorage<
    string | undefined
  >(LANGUAGE_KEY, 'en');

  useInjectCustomTranslations(language);

  const setLanguage = useCallback(
    (language: string | undefined) => {
      setValue(language);
      updateTranslationsLanguage(language);
    },
    [setValue, updateTranslationsLanguage],
  );

  const localizationProps: LocalizationProviderProps<Moment, string> =
    useMemo(() => {
      return {
        adapterLocale: getAdapterLocale(language),
        dateAdapter: AdapterMoment,
        dateLibInstance: getMomentDateLibInstance(language),
      };
    }, [language]);

  return (
    <SelectedLanguageContext.Provider
      value={{
        language,
        setLanguage,
      }}
    >
      <LocalizationProvider {...localizationProps}>
        {children}
      </LocalizationProvider>
    </SelectedLanguageContext.Provider>
  );
};

type ThemePropsType = {
  theme?: Theme;
  children?: ReactNode;
};

export const LocalizedThemeProvider = (props: ThemePropsType) => {
  const { children, theme } = props;

  const {
    i18n: { language },
  } = useTranslation();

  const providedTheme = useTheme();

  const themeWithLocale = useMemo(() => {
    const applyTheme = !!theme ? theme : providedTheme;

    const resolveLocale = <T, M extends { [p: string]: T }, L extends keyof M>(
      locales: M,
      defaultLocale: L,
      language: string,
    ): T => {
      const localeMap = Object.keys(locales).map(
        (
          locale,
        ): {
          locale: L;
          lang: string;
        } => ({
          locale: locale as L,
          lang: locale.slice(0, 2),
        }),
      );

      const locale =
        localeMap.find(
          ({ lang }) => lang === (language === 'fm' ? 'fi' : language),
        )?.locale ?? defaultLocale;

      return locales[locale];
    };

    return createTheme(
      applyTheme,
      resolveLocale(coreLocales, 'enUS', language),
      resolveLocale(dataGridLocales, 'enUS', language),
      resolveLocale(datePickerLocales, 'enUS', language),
    );
  }, [theme, providedTheme, language]);

  return <ThemeProvider theme={themeWithLocale}>{children}</ThemeProvider>;
};
